import { classicFormat, percentFormatCompact } from '@/utils/hooks/format/useFormatNumber';

import { SocialProfileNetworkType } from '@/types/socialProfileType';

import type {
    FBPostTypesType,
    IGPostTypesType,
    IGStoryTypesType,
    LIPostTypesType,
    PIPostTypesType,
    TIPostTypesType,
    TWPostTypesType,
    YTPostTypesType
} from '@/lib/posts/postTypes';
import {
    FB_POST_TYPES,
    IG_POST_TYPES,
    IG_STORY_TYPES,
    LI_POST_TYPES,
    PI_POST_TYPES,
    POST,
    STORY,
    TI_POST_TYPES,
    TW_POST_TYPES,
    YT_POST_TYPES
} from '@/lib/posts/postTypes';
import type {
    FBPostSortType,
    IGPostSortType,
    IGStorySortType,
    LIPostSortType,
    PIPostSortType,
    TIPostSortType,
    TWPostSortType,
    YTPostSortType
} from '@/lib/posts/sortOptionsTypes';
import {
    FACEBOOK,
    INSTAGRAM,
    LINKEDIN,
    PINTEREST,
    TIKTOK_BUSINESS,
    TWITTER,
    YOUTUBE
} from '@/lib/socialProfile';

//default IG config
const DEFAULT_INSTAGRAM_PHOTO_CONFIG = ['likes', 'comments'] as const;
const DEFAULT_INSTAGRAM_VIDEO_CONFIG = ['likes', 'comments'] as const;
const DEFAULT_INSTAGRAM_REEL_CONFIG = ['views', 'likes', 'comments'] as const;

//default FB config
const DEFAULT_FACEBOOK_PHOTO_CONFIG = ['reactions', 'comments', 'engagements'] as const;
const DEFAULT_FACEBOOK_VIDEO_CONFIG = [
    'video_views',
    'reactions',
    'comments',
    'engagements'
] as const;
const DEFAULT_FACEBOOK_REEL_CONFIG = ['plays', 'reactions', 'comments', 'engagements'] as const;

//default Story config
const DEFAULT_STORY_PHOTO_CONFIG = ['reach', 'impressions'] as const;
const DEFAULT_STORY_VIDEO_CONFIG = ['reach', 'impressions'] as const;

//default LI config
const DEFAULT_LINKEDIN_CONFIG = ['reactions', 'comments', 'shares', 'engagements'] as const;
const DEFAULT_VIDEO_LINKEDIN_CONFIG = [
    'video_views',
    'reactions',
    'comments',
    'shares',
    'engagements'
] as const;

//default PI config
const DEFAULT_PINTEREST_CONFIG = [
    'reactions',
    'comments',
    'engagement_rate',
    'impressions'
] as const;

const DEFAULT_VIDEO_PINTEREST_CONFIG = [
    'reactions',
    'comments',
    'engagement_rate',
    'video_views'
] as const;

//default Twitter config
const DEFAULT_TWITTER_CONFIG = ['likes', 'replies', 'retweets'] as const;
const DEFAULT_VIDEO_TWITTER_CONFIG = ['video_views', 'likes', 'replies', 'retweets'] as const;

//default Tiktok config
const DEFAULT_TIKTOK_CONFIG = ['video_views', 'likes', 'comments', 'shares'] as const;

//default Youtube config
const DEFAULT_YOUTUBE_CONFIG = ['views', 'likes'] as const;

export const postFooterStats = {
    [INSTAGRAM]: {
        [IG_POST_TYPES.TYPE_CAROUSEL]: {
            default: [...DEFAULT_INSTAGRAM_PHOTO_CONFIG, 'engagements'],
            saves: [...DEFAULT_INSTAGRAM_PHOTO_CONFIG, 'saves'],
            impressions: [...DEFAULT_INSTAGRAM_PHOTO_CONFIG, 'impressions'],
            reach: [...DEFAULT_INSTAGRAM_PHOTO_CONFIG, 'reach'],
            views: [...DEFAULT_INSTAGRAM_PHOTO_CONFIG, 'views'],
            shares: [...DEFAULT_INSTAGRAM_PHOTO_CONFIG, 'shares'],
            engagement_on_reach: [...DEFAULT_INSTAGRAM_PHOTO_CONFIG, 'engagement_on_reach']
        },
        [IG_POST_TYPES.TYPE_PHOTO]: {
            default: [...DEFAULT_INSTAGRAM_PHOTO_CONFIG, 'engagements'],
            saves: [...DEFAULT_INSTAGRAM_PHOTO_CONFIG, 'saves'],
            impressions: [...DEFAULT_INSTAGRAM_PHOTO_CONFIG, 'impressions'],
            reach: [...DEFAULT_INSTAGRAM_PHOTO_CONFIG, 'reach'],
            views: [...DEFAULT_INSTAGRAM_PHOTO_CONFIG, 'views'],
            shares: [...DEFAULT_INSTAGRAM_PHOTO_CONFIG, 'shares'],
            engagement_on_reach: [...DEFAULT_INSTAGRAM_PHOTO_CONFIG, 'engagement_on_reach']
        },
        [IG_POST_TYPES.TYPE_REEL]: {
            default: [...DEFAULT_INSTAGRAM_REEL_CONFIG, 'engagements'],
            saves: [...DEFAULT_INSTAGRAM_REEL_CONFIG, 'saves'],
            impressions: [...DEFAULT_INSTAGRAM_REEL_CONFIG, 'impressions'],
            reach: [...DEFAULT_INSTAGRAM_REEL_CONFIG, 'reach'],
            views: [...DEFAULT_INSTAGRAM_REEL_CONFIG],
            shares: [...DEFAULT_INSTAGRAM_REEL_CONFIG, 'shares'],
            engagement_on_reach: [...DEFAULT_INSTAGRAM_REEL_CONFIG, 'engagement_on_reach']
        },
        [IG_POST_TYPES.TYPE_VIDEO]: {
            default: [...DEFAULT_INSTAGRAM_VIDEO_CONFIG, 'engagements'],
            saves: [...DEFAULT_INSTAGRAM_VIDEO_CONFIG, 'saves'],
            impressions: [...DEFAULT_INSTAGRAM_VIDEO_CONFIG, 'impressions'],
            reach: [...DEFAULT_INSTAGRAM_VIDEO_CONFIG, 'reach'],
            views: [...DEFAULT_INSTAGRAM_VIDEO_CONFIG, 'views'],
            shares: [...DEFAULT_INSTAGRAM_VIDEO_CONFIG, 'shares'],
            engagement_on_reach: [...DEFAULT_INSTAGRAM_VIDEO_CONFIG, 'engagement_on_reach']
        },
        [IG_POST_TYPES.TYPE_IG_TV]: {
            default: [...DEFAULT_INSTAGRAM_VIDEO_CONFIG, 'engagements'],
            saves: [...DEFAULT_INSTAGRAM_VIDEO_CONFIG, 'saves'],
            impressions: [...DEFAULT_INSTAGRAM_VIDEO_CONFIG, 'impressions'],
            reach: [...DEFAULT_INSTAGRAM_VIDEO_CONFIG, 'reach'],
            views: [...DEFAULT_INSTAGRAM_VIDEO_CONFIG, 'views'],
            shares: [...DEFAULT_INSTAGRAM_VIDEO_CONFIG, 'shares'],
            engagement_on_reach: [...DEFAULT_INSTAGRAM_VIDEO_CONFIG, 'engagement_on_reach']
        }
    },
    [FACEBOOK]: {
        [FB_POST_TYPES.TYPE_PHOTO]: {
            default: DEFAULT_FACEBOOK_PHOTO_CONFIG,
            engagement_rate: [...DEFAULT_FACEBOOK_PHOTO_CONFIG, 'engagement_rate'],
            engagements: [...DEFAULT_FACEBOOK_PHOTO_CONFIG, 'engagements'],
            clicks: [...DEFAULT_FACEBOOK_PHOTO_CONFIG, 'clicks'],
            reach: [...DEFAULT_FACEBOOK_PHOTO_CONFIG, 'reach'],
            shares: [...DEFAULT_FACEBOOK_PHOTO_CONFIG, 'shares'],
            impressions: [...DEFAULT_FACEBOOK_PHOTO_CONFIG, 'impressions'],
            engagement_on_reach: [
                ...DEFAULT_FACEBOOK_PHOTO_CONFIG.filter((entry) => entry !== 'engagements'),
                'engagement_on_reach'
            ],
            like_reactions: [...DEFAULT_FACEBOOK_PHOTO_CONFIG, 'like_reactions'],
            love_reactions: [...DEFAULT_FACEBOOK_PHOTO_CONFIG, 'love_reactions'],
            haha_reactions: [...DEFAULT_FACEBOOK_PHOTO_CONFIG, 'haha_reactions'],
            wow_reactions: [...DEFAULT_FACEBOOK_PHOTO_CONFIG, 'wow_reactions'],
            sad_reactions: [...DEFAULT_FACEBOOK_PHOTO_CONFIG, 'sad_reactions'],
            angry_reactions: [...DEFAULT_FACEBOOK_PHOTO_CONFIG, 'angry_reactions']
        },
        [FB_POST_TYPES.TYPE_STATUS]: {
            default: DEFAULT_FACEBOOK_PHOTO_CONFIG,
            engagement_rate: [...DEFAULT_FACEBOOK_PHOTO_CONFIG, 'engagement_rate'],
            engagements: [...DEFAULT_FACEBOOK_PHOTO_CONFIG, 'engagements'],
            clicks: [...DEFAULT_FACEBOOK_PHOTO_CONFIG, 'clicks'],
            reach: [...DEFAULT_FACEBOOK_PHOTO_CONFIG, 'reach'],
            impressions: [...DEFAULT_FACEBOOK_PHOTO_CONFIG, 'impressions'],
            engagement_on_reach: [
                ...DEFAULT_FACEBOOK_PHOTO_CONFIG.filter((entry) => entry !== 'engagements'),
                'engagement_on_reach'
            ],
            like_reactions: [...DEFAULT_FACEBOOK_PHOTO_CONFIG, 'like_reactions'],
            love_reactions: [...DEFAULT_FACEBOOK_PHOTO_CONFIG, 'love_reactions'],
            haha_reactions: [...DEFAULT_FACEBOOK_PHOTO_CONFIG, 'haha_reactions'],
            wow_reactions: [...DEFAULT_FACEBOOK_PHOTO_CONFIG, 'wow_reactions'],
            sad_reactions: [...DEFAULT_FACEBOOK_PHOTO_CONFIG, 'sad_reactions'],
            angry_reactions: [...DEFAULT_FACEBOOK_PHOTO_CONFIG, 'angry_reactions']
        },
        [FB_POST_TYPES.TYPE_CAROUSEL]: {
            default: DEFAULT_FACEBOOK_PHOTO_CONFIG,
            engagement_rate: [...DEFAULT_FACEBOOK_PHOTO_CONFIG, 'engagement_rate'],
            engagements: [...DEFAULT_FACEBOOK_PHOTO_CONFIG, 'engagements'],
            clicks: [...DEFAULT_FACEBOOK_PHOTO_CONFIG, 'clicks'],
            reach: [...DEFAULT_FACEBOOK_PHOTO_CONFIG, 'reach'],
            impressions: [...DEFAULT_FACEBOOK_PHOTO_CONFIG, 'impressions'],
            engagement_on_reach: [
                ...DEFAULT_FACEBOOK_PHOTO_CONFIG.filter((entry) => entry !== 'engagements'),
                'engagement_on_reach'
            ],
            like_reactions: [...DEFAULT_FACEBOOK_PHOTO_CONFIG, 'like_reactions'],
            love_reactions: [...DEFAULT_FACEBOOK_PHOTO_CONFIG, 'love_reactions'],
            haha_reactions: [...DEFAULT_FACEBOOK_PHOTO_CONFIG, 'haha_reactions'],
            wow_reactions: [...DEFAULT_FACEBOOK_PHOTO_CONFIG, 'wow_reactions'],
            sad_reactions: [...DEFAULT_FACEBOOK_PHOTO_CONFIG, 'sad_reactions'],
            angry_reactions: [...DEFAULT_FACEBOOK_PHOTO_CONFIG, 'angry_reactions']
        },
        [FB_POST_TYPES.TYPE_VIDEO]: {
            default: DEFAULT_FACEBOOK_VIDEO_CONFIG,
            engagement_rate: [...DEFAULT_FACEBOOK_VIDEO_CONFIG, 'engagement_rate'],
            engagements: [...DEFAULT_FACEBOOK_VIDEO_CONFIG, 'engagements'],
            clicks: [...DEFAULT_FACEBOOK_VIDEO_CONFIG, 'clicks'],
            reach: [...DEFAULT_FACEBOOK_VIDEO_CONFIG, 'reach'],
            shares: [...DEFAULT_FACEBOOK_VIDEO_CONFIG, 'shares'],
            impressions: [...DEFAULT_FACEBOOK_VIDEO_CONFIG, 'impressions'],
            engagement_on_reach: [
                ...DEFAULT_FACEBOOK_VIDEO_CONFIG.filter((entry) => entry !== 'engagements'),
                'engagement_on_reach'
            ],
            like_reactions: [...DEFAULT_FACEBOOK_VIDEO_CONFIG, 'like_reactions'],
            love_reactions: [...DEFAULT_FACEBOOK_VIDEO_CONFIG, 'love_reactions'],
            haha_reactions: [...DEFAULT_FACEBOOK_VIDEO_CONFIG, 'haha_reactions'],
            wow_reactions: [...DEFAULT_FACEBOOK_VIDEO_CONFIG, 'wow_reactions'],
            sad_reactions: [...DEFAULT_FACEBOOK_VIDEO_CONFIG, 'sad_reactions'],
            angry_reactions: [...DEFAULT_FACEBOOK_VIDEO_CONFIG, 'angry_reactions'],
            video_views_10s: ['video_views_10s', ...DEFAULT_FACEBOOK_VIDEO_CONFIG].filter(
                (e) => e !== 'video_views'
            ),
            video_views_complete: ['video_views_complete', ...DEFAULT_FACEBOOK_VIDEO_CONFIG].filter(
                (e) => e !== 'video_views'
            )
        },
        [FB_POST_TYPES.TYPE_STORY]: {
            default: DEFAULT_FACEBOOK_VIDEO_CONFIG,
            engagement_rate: [...DEFAULT_FACEBOOK_VIDEO_CONFIG, 'engagement_rate'],
            engagements: [...DEFAULT_FACEBOOK_VIDEO_CONFIG, 'engagements'],
            clicks: [...DEFAULT_FACEBOOK_VIDEO_CONFIG, 'clicks'],
            reach: [...DEFAULT_FACEBOOK_VIDEO_CONFIG, 'reach'],
            shares: [...DEFAULT_FACEBOOK_VIDEO_CONFIG, 'shares'],
            impressions: [...DEFAULT_FACEBOOK_VIDEO_CONFIG, 'impressions'],
            engagement_on_reach: [
                ...DEFAULT_FACEBOOK_VIDEO_CONFIG.filter((entry) => entry !== 'engagements'),
                'engagement_on_reach'
            ],
            like_reactions: [...DEFAULT_FACEBOOK_VIDEO_CONFIG, 'like_reactions'],
            love_reactions: [...DEFAULT_FACEBOOK_VIDEO_CONFIG, 'love_reactions'],
            haha_reactions: [...DEFAULT_FACEBOOK_VIDEO_CONFIG, 'haha_reactions'],
            wow_reactions: [...DEFAULT_FACEBOOK_VIDEO_CONFIG, 'wow_reactions'],
            sad_reactions: [...DEFAULT_FACEBOOK_VIDEO_CONFIG, 'sad_reactions'],
            angry_reactions: [...DEFAULT_FACEBOOK_VIDEO_CONFIG, 'angry_reactions']
        },
        [FB_POST_TYPES.TYPE_REEL]: {
            default: DEFAULT_FACEBOOK_REEL_CONFIG,
            engagement_rate: [...DEFAULT_FACEBOOK_REEL_CONFIG, 'engagement_rate'],
            engagements: [...DEFAULT_FACEBOOK_REEL_CONFIG, 'engagements'],
            clicks: [...DEFAULT_FACEBOOK_REEL_CONFIG, 'clicks'],
            reach: [...DEFAULT_FACEBOOK_REEL_CONFIG, 'reach'],
            shares: [...DEFAULT_FACEBOOK_REEL_CONFIG, 'shares'],
            impressions: [...DEFAULT_FACEBOOK_REEL_CONFIG, 'impressions'],
            engagement_on_reach: [
                ...DEFAULT_FACEBOOK_REEL_CONFIG.filter((entry) => entry !== 'engagements'),
                'engagement_on_reach'
            ],
            like_reactions: [...DEFAULT_FACEBOOK_REEL_CONFIG, 'like_reactions'],
            love_reactions: [...DEFAULT_FACEBOOK_REEL_CONFIG, 'love_reactions'],
            haha_reactions: [...DEFAULT_FACEBOOK_REEL_CONFIG, 'haha_reactions'],
            wow_reactions: [...DEFAULT_FACEBOOK_REEL_CONFIG, 'wow_reactions'],
            sad_reactions: [...DEFAULT_FACEBOOK_REEL_CONFIG, 'sad_reactions'],
            angry_reactions: [...DEFAULT_FACEBOOK_REEL_CONFIG, 'angry_reactions']
        }
    },
    [LINKEDIN]: {
        [LI_POST_TYPES.TYPE_PHOTO]: {
            default: DEFAULT_LINKEDIN_CONFIG,
            impressions: [
                ...DEFAULT_LINKEDIN_CONFIG.filter((entry) => entry !== 'engagements'),
                'impressions'
            ],
            engagement_rate: [
                ...DEFAULT_LINKEDIN_CONFIG.filter((entry) => entry !== 'engagements'),
                'engagement_rate'
            ],
            clicks: [
                ...DEFAULT_LINKEDIN_CONFIG.filter((entry) => entry !== 'engagements'),
                'clicks'
            ],
            reach: [...DEFAULT_LINKEDIN_CONFIG.filter((entry) => entry !== 'engagements'), 'reach']
        },
        [LI_POST_TYPES.TYPE_VIDEO]: {
            default: DEFAULT_VIDEO_LINKEDIN_CONFIG,
            impressions: [
                ...DEFAULT_VIDEO_LINKEDIN_CONFIG.filter((entry) => entry !== 'engagements'),
                'impressions'
            ],
            engagement_rate: [
                ...DEFAULT_VIDEO_LINKEDIN_CONFIG.filter((entry) => entry !== 'engagements'),
                'engagement_rate'
            ],
            clicks: [
                ...DEFAULT_LINKEDIN_CONFIG.filter((entry) => entry !== 'engagements'),
                'clicks'
            ],
            reach: [...DEFAULT_LINKEDIN_CONFIG.filter((entry) => entry !== 'engagements'), 'reach']
        },
        [LI_POST_TYPES.TYPE_CAROUSEL]: {
            default: DEFAULT_LINKEDIN_CONFIG,
            impressions: [
                ...DEFAULT_LINKEDIN_CONFIG.filter((entry) => entry !== 'engagements'),
                'impressions'
            ],
            engagement_rate: [
                ...DEFAULT_LINKEDIN_CONFIG.filter((entry) => entry !== 'engagements'),
                'engagement_rate'
            ],
            clicks: [
                ...DEFAULT_LINKEDIN_CONFIG.filter((entry) => entry !== 'engagements'),
                'clicks'
            ],
            reach: [...DEFAULT_LINKEDIN_CONFIG.filter((entry) => entry !== 'engagements'), 'reach']
        },
        [LI_POST_TYPES.TYPE_STATUS]: {
            default: DEFAULT_LINKEDIN_CONFIG,
            impressions: [
                ...DEFAULT_LINKEDIN_CONFIG.filter((entry) => entry !== 'engagements'),
                'impressions'
            ],
            engagement_rate: [
                ...DEFAULT_LINKEDIN_CONFIG.filter((entry) => entry !== 'engagements'),
                'engagement_rate'
            ],
            clicks: [
                ...DEFAULT_LINKEDIN_CONFIG.filter((entry) => entry !== 'engagements'),
                'clicks'
            ],
            reach: [...DEFAULT_LINKEDIN_CONFIG.filter((entry) => entry !== 'engagements'), 'reach']
        },
        [LI_POST_TYPES.TYPE_LINK]: {
            default: DEFAULT_LINKEDIN_CONFIG,
            impressions: [
                ...DEFAULT_LINKEDIN_CONFIG.filter((entry) => entry !== 'engagements'),
                'impressions'
            ],
            engagement_rate: [
                ...DEFAULT_LINKEDIN_CONFIG.filter((entry) => entry !== 'engagements'),
                'engagement_rate'
            ],
            clicks: [
                ...DEFAULT_LINKEDIN_CONFIG.filter((entry) => entry !== 'engagements'),
                'clicks'
            ],
            reach: [...DEFAULT_LINKEDIN_CONFIG.filter((entry) => entry !== 'engagements'), 'reach']
        },
        [LI_POST_TYPES.TYPE_DOCUMENT]: {
            default: DEFAULT_LINKEDIN_CONFIG,
            impressions: [
                ...DEFAULT_LINKEDIN_CONFIG.filter((entry) => entry !== 'engagements'),
                'impressions'
            ],
            engagement_rate: [
                ...DEFAULT_LINKEDIN_CONFIG.filter((entry) => entry !== 'engagements'),
                'engagement_rate'
            ],
            clicks: [
                ...DEFAULT_LINKEDIN_CONFIG.filter((entry) => entry !== 'engagements'),
                'clicks'
            ],
            reach: [...DEFAULT_LINKEDIN_CONFIG.filter((entry) => entry !== 'engagements'), 'reach']
        },
        [LI_POST_TYPES.TYPE_POLL]: {
            default: DEFAULT_LINKEDIN_CONFIG,
            impressions: [
                ...DEFAULT_LINKEDIN_CONFIG.filter((entry) => entry !== 'engagements'),
                'impressions'
            ],
            engagement_rate: [
                ...DEFAULT_LINKEDIN_CONFIG.filter((entry) => entry !== 'engagements'),
                'engagement_rate'
            ],
            clicks: [
                ...DEFAULT_LINKEDIN_CONFIG.filter((entry) => entry !== 'engagements'),
                'clicks'
            ],
            reach: [...DEFAULT_LINKEDIN_CONFIG.filter((entry) => entry !== 'engagements'), 'reach']
        }
    },
    [PINTEREST]: {
        [PI_POST_TYPES.TYPE_IMAGE]: {
            default: DEFAULT_PINTEREST_CONFIG,
            engagement_on_impression: [
                ...DEFAULT_PINTEREST_CONFIG.filter((entry) => entry !== 'engagement_rate'),
                'engagement_on_impression'
            ],
            engagements: [
                ...DEFAULT_PINTEREST_CONFIG.filter((entry) => entry !== 'engagement_rate'),
                'engagements'
            ],
            saves: [
                ...DEFAULT_PINTEREST_CONFIG.filter((entry) => entry !== 'impressions'),
                'saves'
            ],
            pin_clicks: [
                ...DEFAULT_PINTEREST_CONFIG.filter((entry) => entry !== 'impressions'),
                'pin_clicks'
            ]
        },
        [PI_POST_TYPES.TYPE_MULTIPLE_IMAGES]: {
            default: DEFAULT_PINTEREST_CONFIG,
            engagement_on_impression: [
                ...DEFAULT_PINTEREST_CONFIG.filter((entry) => entry !== 'engagement_rate'),
                'engagement_on_impression'
            ],
            engagements: [
                ...DEFAULT_PINTEREST_CONFIG.filter((entry) => entry !== 'engagement_rate'),
                'engagements'
            ],
            saves: [
                ...DEFAULT_PINTEREST_CONFIG.filter((entry) => entry !== 'impressions'),
                'saves'
            ],
            pin_clicks: [
                ...DEFAULT_PINTEREST_CONFIG.filter((entry) => entry !== 'impressions'),
                'pin_clicks'
            ]
        },
        [PI_POST_TYPES.TYPE_MULTIPLE_MIXED]: {
            default: DEFAULT_PINTEREST_CONFIG,
            engagement_on_impression: [
                ...DEFAULT_PINTEREST_CONFIG.filter((entry) => entry !== 'engagement_rate'),
                'engagement_on_impression'
            ],
            engagements: [
                ...DEFAULT_PINTEREST_CONFIG.filter((entry) => entry !== 'engagement_rate'),
                'engagements'
            ],
            saves: [
                ...DEFAULT_PINTEREST_CONFIG.filter((entry) => entry !== 'impressions'),
                'saves'
            ],
            pin_clicks: [
                ...DEFAULT_PINTEREST_CONFIG.filter((entry) => entry !== 'impressions'),
                'pin_clicks'
            ]
        },
        [PI_POST_TYPES.TYPE_MULTIPLE_VIDEOS]: {
            default: DEFAULT_PINTEREST_CONFIG,
            engagement_on_impression: [
                ...DEFAULT_PINTEREST_CONFIG.filter((entry) => entry !== 'engagement_rate'),
                'engagement_on_impression'
            ],
            engagements: [
                ...DEFAULT_PINTEREST_CONFIG.filter((entry) => entry !== 'engagement_rate'),
                'engagements'
            ],
            saves: [
                ...DEFAULT_PINTEREST_CONFIG.filter((entry) => entry !== 'impressions'),
                'saves'
            ],
            pin_clicks: [
                ...DEFAULT_PINTEREST_CONFIG.filter((entry) => entry !== 'impressions'),
                'pin_clicks'
            ]
        },
        [PI_POST_TYPES.TYPE_VIDEO]: {
            default: DEFAULT_VIDEO_PINTEREST_CONFIG,
            engagement_on_impression: [
                ...DEFAULT_VIDEO_PINTEREST_CONFIG.filter((entry) => entry !== 'engagement_rate'),
                'engagement_on_impression'
            ],
            engagements: [
                ...DEFAULT_VIDEO_PINTEREST_CONFIG.filter((entry) => entry !== 'engagement_rate'),
                'engagements'
            ],
            impressions: [
                ...DEFAULT_VIDEO_PINTEREST_CONFIG.filter((entry) => entry !== 'engagement_rate'),
                'impressions'
            ],
            saves: [
                ...DEFAULT_VIDEO_PINTEREST_CONFIG.filter((entry) => entry !== 'video_views'),
                'saves'
            ],
            pin_clicks: [
                ...DEFAULT_VIDEO_PINTEREST_CONFIG.filter((entry) => entry !== 'video_views'),
                'pin_clicks'
            ]
        }
    },
    [TWITTER]: {
        [TW_POST_TYPES.TYPE_PHOTO]: {
            default: DEFAULT_TWITTER_CONFIG,
            engagement_rate: [...DEFAULT_TWITTER_CONFIG, 'engagement_rate'],
            impressions: [...DEFAULT_TWITTER_CONFIG, 'impressions']
        },
        [TW_POST_TYPES.TYPE_VIDEO]: {
            default: DEFAULT_VIDEO_TWITTER_CONFIG,
            engagement_rate: [...DEFAULT_VIDEO_TWITTER_CONFIG, 'engagement_rate'],
            impressions: [...DEFAULT_VIDEO_TWITTER_CONFIG, 'impressions']
        },
        [TW_POST_TYPES.TYPE_CAROUSEL]: {
            default: DEFAULT_TWITTER_CONFIG,
            engagement_rate: [...DEFAULT_TWITTER_CONFIG, 'engagement_rate'],
            impressions: [...DEFAULT_TWITTER_CONFIG, 'impressions']
        },
        [TW_POST_TYPES.TYPE_STATUS]: {
            default: DEFAULT_TWITTER_CONFIG,
            engagement_rate: [...DEFAULT_TWITTER_CONFIG, 'engagement_rate'],
            impressions: [...DEFAULT_TWITTER_CONFIG, 'impressions']
        },
        [TW_POST_TYPES.TYPE_LINK]: {
            default: DEFAULT_TWITTER_CONFIG,
            engagement_rate: [...DEFAULT_TWITTER_CONFIG, 'engagement_rate'],
            impressions: [...DEFAULT_TWITTER_CONFIG, 'impressions']
        },
        [TW_POST_TYPES.TYPE_RETWEET]: {
            default: DEFAULT_TWITTER_CONFIG,
            engagement_rate: [...DEFAULT_TWITTER_CONFIG, 'engagement_rate'],
            impressions: [...DEFAULT_TWITTER_CONFIG, 'impressions']
        },
        [TW_POST_TYPES.TYPE_RETWEET_COMMENT]: {
            default: DEFAULT_TWITTER_CONFIG,
            engagement_rate: [...DEFAULT_TWITTER_CONFIG, 'engagement_rate'],
            impressions: [...DEFAULT_TWITTER_CONFIG, 'impressions']
        },
        [TW_POST_TYPES.TYPE_REPLY]: {
            default: DEFAULT_TWITTER_CONFIG,
            engagement_rate: [...DEFAULT_TWITTER_CONFIG, 'engagement_rate'],
            impressions: [...DEFAULT_TWITTER_CONFIG, 'impressions']
        },
        [TW_POST_TYPES.TYPE_POLL]: {
            default: DEFAULT_TWITTER_CONFIG,
            engagement_rate: [...DEFAULT_TWITTER_CONFIG, 'engagement_rate'],
            impressions: [...DEFAULT_TWITTER_CONFIG, 'impressions']
        }
    },
    [TIKTOK_BUSINESS]: {
        [TI_POST_TYPES.TYPE_VIDEO]: {
            default: DEFAULT_TIKTOK_CONFIG,
            engagements: [
                ...DEFAULT_TIKTOK_CONFIG.filter((entry) => entry !== 'shares'),
                'engagements'
            ],
            engagement_on_views: [
                ...DEFAULT_TIKTOK_CONFIG.filter((entry) => entry !== 'shares'),
                'engagement_on_views'
            ]
        }
    },
    [YOUTUBE]: {
        [YT_POST_TYPES.TYPE_VIDEO]: {
            default: DEFAULT_YOUTUBE_CONFIG,
            dislikes: [...DEFAULT_YOUTUBE_CONFIG, 'dislikes'],
            comments: [...DEFAULT_YOUTUBE_CONFIG, 'comments'],
            shares: [...DEFAULT_YOUTUBE_CONFIG, 'shares']
        },
        [YT_POST_TYPES.TYPE_SHORT]: {
            default: DEFAULT_YOUTUBE_CONFIG,
            dislikes: [...DEFAULT_YOUTUBE_CONFIG, 'dislikes'],
            comments: [...DEFAULT_YOUTUBE_CONFIG, 'comments'],
            shares: [...DEFAULT_YOUTUBE_CONFIG, 'shares']
        }
    }
} as const;

export const storyFooterStats = {
    [INSTAGRAM]: {
        [IG_STORY_TYPES.TYPE_PHOTO]: {
            default: DEFAULT_STORY_PHOTO_CONFIG,
            completed_rate: [...DEFAULT_STORY_PHOTO_CONFIG, 'completed_rate'],
            replies: [...DEFAULT_STORY_PHOTO_CONFIG, 'replies'],
            impressions: ['impressions', 'reach'],
            reach: DEFAULT_STORY_PHOTO_CONFIG,
            reach_rate: ['reach', 'reach_rate'],
            exits: [...DEFAULT_STORY_PHOTO_CONFIG, 'exits'],
            exits_rate: [...DEFAULT_STORY_PHOTO_CONFIG, 'exits_rate'],
            taps_forward: [...DEFAULT_STORY_PHOTO_CONFIG, 'taps_forward'],
            taps_back: [...DEFAULT_STORY_PHOTO_CONFIG, 'taps_back']
        },
        [IG_STORY_TYPES.TYPE_VIDEO]: {
            default: DEFAULT_STORY_VIDEO_CONFIG,
            completed_rate: [...DEFAULT_STORY_PHOTO_CONFIG, 'completed_rate'],
            impressions: ['impressions', 'reach'],
            reach: ['reach', 'impressions'],
            reach_rate: ['reach', 'reach_rate'],
            replies: [...DEFAULT_STORY_PHOTO_CONFIG, 'replies'],
            exits: [...DEFAULT_STORY_PHOTO_CONFIG, 'exits'],
            exits_rate: [...DEFAULT_STORY_PHOTO_CONFIG, 'exits_rate'],
            taps_forward: [...DEFAULT_STORY_PHOTO_CONFIG, 'taps_forward'],
            taps_back: [...DEFAULT_STORY_PHOTO_CONFIG, 'taps_back']
        }
    },
    [TIKTOK_BUSINESS]: {
        [TI_POST_TYPES.TYPE_VIDEO]: {
            default: DEFAULT_TIKTOK_CONFIG,
            engagements: [
                ...DEFAULT_TIKTOK_CONFIG.filter((entry) => entry !== 'shares'),
                'engagements'
            ],
            engagement_on_views: [
                ...DEFAULT_TIKTOK_CONFIG.filter((entry) => entry !== 'shares'),
                'engagement_on_views'
            ]
        }
    }
} as const;

type getStatIconProps =
    | 'engagements'
    | 'engagement_on_impression'
    | 'engagement_on_reach'
    | 'engagement_rate'
    | 'engagement_on_views'
    | 'like_reactions'
    | 'reactions'
    | 'comments'
    | 'love_reactions'
    | 'haha_reactions'
    | 'wow_reactions'
    | 'sad_reactions'
    | 'angry_reactions'
    | 'likes'
    | 'dislikes'
    | 'video_views'
    | 'video_views_10s'
    | 'video_views_complete'
    | 'reach'
    | 'reach_rate'
    | 'saves'
    | 'shares'
    | 'impressions'
    | 'completed_rate'
    | 'completed'
    | 'replies'
    | 'exits'
    | 'exits_rate'
    | 'taps_forward'
    | 'taps_back'
    | 'clicks'
    | 'retweets'
    | 'plays'
    | 'views'
    | 'pin_clicks';

export function getStatIcon(stat: getStatIconProps, network: SocialProfileNetworkType) {
    switch (stat) {
        case 'engagements':
        case 'engagement_on_impression':
        case 'engagement_on_reach':
        case 'engagement_rate':
        case 'engagement_on_views':
            return 'engagement';
        case 'like_reactions':
            return 'like-reaction';
        case 'reactions':
            return 'reaction';
        case 'comments':
            return 'comments';
        case 'love_reactions':
            return 'love-reaction';
        case 'haha_reactions':
            return 'haha-reaction';
        case 'wow_reactions':
            return 'wow-reaction';
        case 'sad_reactions':
            return 'sad-reaction';
        case 'angry_reactions':
            return 'angry-reaction';
        case 'likes':
            return network === YOUTUBE ? 'reaction' : 'likes';
        case 'dislikes':
            return 'dislike';
        case 'video_views':
        case 'views':
        case 'video_views_10s':
        case 'video_views_complete':
            return 'play';
        case 'reach':
        case 'reach_rate':
            return 'reach';
        case 'saves':
            return 'collections';
        case 'replies':
            return 'replies';
        case 'exits':
        case 'exits_rate':
            return 'exit';
        case 'shares':
            return 'share';
        case 'impressions':
            return 'impressions';
        case 'completed':
        case 'completed_rate':
            return 'completed';
        case 'taps_forward':
            return 'arrow-right';
        case 'taps_back':
            return 'arrow-left';
        case 'clicks':
        case 'pin_clicks':
            return 'click';
        case 'retweets':
            return 'retweet';
        case 'plays':
            return 'play';
        default:
            return null;
    }
}

export function getStatLabel(stat: getStatIconProps, network: SocialProfileNetworkType) {
    switch (stat) {
        case 'views':
            return network === YOUTUBE ? 'youtube_views' : '';
        case 'likes':
            return network === YOUTUBE ? 'youtube_likes' : '';
        case 'dislikes':
            return network === YOUTUBE ? 'youtube_dislikes' : '';
        case 'comments':
            return network === YOUTUBE ? 'youtube_comments' : '';
        case 'shares':
            return network === YOUTUBE ? 'youtube_shares' : '';
    }
}

export function getNumberFormatOptions(stat) {
    switch (stat) {
        case 'engagement_on_impression':
        case 'engagement_on_reach':
        case 'engagement_rate':
        case 'reach_rate':
        case 'engagement_on_views':
            return percentFormatCompact;
        case 'reactions':
        case 'comments':
        case 'engagements':
        case 'likes':
        case 'views':
        case 'reach':
        case 'plays':
            return classicFormat;
        default:
            return classicFormat;
    }
}

//Union type to get the most precise type definition for the footer stats
type getFooterStatsParams =
    | {
          postType: typeof STORY;
          type: IGStoryTypesType;
          filter?: IGStorySortType;
          network: typeof INSTAGRAM;
      }
    | {
          postType: typeof POST;
          type: IGPostTypesType;
          filter?: IGPostSortType;
          network: typeof INSTAGRAM;
      }
    | {
          postType: typeof POST;
          type: FBPostTypesType;
          filter?: FBPostSortType;
          network: typeof FACEBOOK;
      }
    | {
          postType: typeof POST;
          type: TWPostTypesType;
          filter?: TWPostSortType;
          network: typeof TWITTER;
      }
    | {
          postType: typeof POST;
          type: LIPostTypesType;
          filter?: LIPostSortType;
          network: typeof LINKEDIN;
      }
    | {
          postType: typeof POST;
          type: TIPostTypesType;
          filter?: TIPostSortType;
          network: typeof TIKTOK_BUSINESS;
      }
    | {
          postType: typeof POST;
          type: YTPostTypesType;
          filter?: YTPostSortType;
          network: typeof YOUTUBE;
      }
    | {
          postType: typeof POST;
          type: PIPostTypesType;
          filter?: PIPostSortType;
          network: typeof PINTEREST;
      };

export function getFooterStats({ postType, type, filter, network }: getFooterStatsParams) {
    switch (postType) {
        case STORY:
            return (
                storyFooterStats?.[network]?.[type]?.[filter] ??
                storyFooterStats?.[network]?.[type]?.default
            );
        case POST:
            return (
                postFooterStats?.[network]?.[type]?.[filter] ??
                postFooterStats?.[network]?.[type]?.default
            );
        default:
            return [];
    }
}
